import React, { useEffect, useState } from "react";
import axios from "axios";
import { useApp } from "../context/AppContext";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft, Package } from "lucide-react";

const ACTIVE_STATUSES = ["pending", "confirmed", "ready"];

export default function PickList() {
  const { API, user, authHeaders } = useApp();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");  // all | pending | confirmed | ready

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/orders`, { headers: authHeaders() })
      .then(r => setOrders(r.data.filter(o => ACTIVE_STATUSES.includes(o.status))))
      .catch(err => console.error(err));
  }, [user, API, authHeaders]);

  if (!user) return <div className="p-20 text-center"><Link to="/login" className="btn-primary">SIGN IN</Link></div>;
  if (user.role !== "admin" && user.role !== "owner") return <div className="p-20 text-center font-mono">Admins only.</div>;

  const visible = filter === "all" ? orders : orders.filter(o => o.status === filter);

  // Flatten items across visible orders, group by category for aisle-friendly picking
  const itemsByCategory = {};
  visible.forEach(o => {
    o.items.forEach(it => {
      const cat = it.category || "General";
      if (!itemsByCategory[cat]) itemsByCategory[cat] = [];
      itemsByCategory[cat].push({ ...it, order_id: o.order_id, customer_name: o.customer_name });
    });
  });
  const sortedCategories = Object.keys(itemsByCategory).sort();

  const today = new Date().toLocaleString("en-US", { dateStyle: "full", timeStyle: "short" });

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="picklist-page">
      {/* Print-hidden controls */}
      <div className="no-print flex flex-wrap items-center gap-3 mb-6">
        <Link to="/admin" className="btn-outline inline-flex items-center gap-2" data-testid="back-to-admin"><ArrowLeft size={16}/> BACK</Link>
        <div className="flex gap-2 ml-auto">
          {["all","pending","confirmed","ready"].map(f => (
            <button key={f} onClick={()=>setFilter(f)}
              className={`px-3 py-2 font-mono text-xs uppercase tracking-wider border-2 border-[var(--ink)] ${filter===f?'bg-[var(--ink)] text-[var(--paper)]':''}`}
              data-testid={`pl-filter-${f}`}>{f === "all" ? "ALL ACTIVE" : f}</button>
          ))}
        </div>
        <button onClick={()=>window.print()} className="btn-primary inline-flex items-center gap-2" data-testid="print-btn"><Printer size={16}/> PRINT</button>
      </div>

      {/* Header */}
      <div className="border-b-2 border-[var(--ink)] pb-4 mb-6">
        <div className="flex items-baseline justify-between">
          <h1 className="font-display text-4xl">PICK LIST</h1>
          <div className="font-mono text-xs text-[var(--ink-2)]">{today}</div>
        </div>
        <div className="font-mono text-xs mt-1">LeCroy Wholesale · 800 McFarland Ave, Rossville GA · 423-645-7402</div>
        <div className="font-mono text-sm mt-2">{visible.length} order(s) · {visible.reduce((s,o)=>s+o.items.reduce((x,i)=>x+i.qty,0),0)} total items</div>
      </div>

      {visible.length === 0 ? (
        <div className="text-center py-20 font-mono" data-testid="picklist-empty">No active orders to pick.</div>
      ) : (
        <>
          {/* ORDER-BY-ORDER VIEW */}
          <section className="mb-10">
            <h2 className="font-display text-2xl mb-3 border-b-2 border-[var(--ink)] pb-1">BY ORDER</h2>
            <div className="space-y-5">
              {visible.map(o => (
                <div key={o.order_id} className="border-2 border-[var(--ink)] p-4 break-inside-avoid" data-testid={`pl-order-${o.order_id}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-[var(--ink-2)] pb-2 mb-2">
                    <div>
                      <div className="font-display text-lg">#{o.order_id} <span className="font-mono text-xs uppercase bg-[var(--accent-2)] px-2 py-0.5 border border-[var(--ink)] ml-2">{o.status}</span></div>
                      <div className="font-mono text-xs">{o.customer_name} · {o.customer_phone}</div>
                    </div>
                    <div className="font-mono text-xs text-right">
                      <div>{new Date(o.created_at).toLocaleString()}</div>
                      <div>{o.payment_method.replace('_',' ')} · ${o.total.toFixed(2)} {o.payment_status === "paid" && <strong>· PAID</strong>}</div>
                    </div>
                  </div>
                  <table className="w-full font-mono text-sm">
                    <thead className="border-b border-[var(--ink-2)]">
                      <tr className="text-left text-xs uppercase">
                        <th className="py-1 w-12">Qty</th>
                        <th className="py-1">Item</th>
                        <th className="py-1 w-28 text-right">Pick ☐</th>
                      </tr>
                    </thead>
                    <tbody>
                      {o.items.map((it, idx) => (
                        <tr key={`${o.order_id}-${idx}`} className="border-b border-[var(--muted)]">
                          <td className="py-1.5 font-bold">×{it.qty}</td>
                          <td className="py-1.5">{it.name}</td>
                          <td className="py-1.5 text-right"><span className="inline-block w-5 h-5 border-2 border-[var(--ink)]"></span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {o.notes && <div className="font-mono text-xs italic mt-2">Note: {o.notes}</div>}
                </div>
              ))}
            </div>
          </section>

          {/* AISLE-GROUPED CATEGORY VIEW */}
          <section className="page-break">
            <h2 className="font-display text-2xl mb-3 border-b-2 border-[var(--ink)] pb-1">BY CATEGORY (AISLE SWEEP)</h2>
            <div className="space-y-4">
              {sortedCategories.map(cat => {
                // Aggregate same items
                const agg = {};
                itemsByCategory[cat].forEach(it => {
                  if (!agg[it.product_id]) agg[it.product_id] = { ...it, qty: 0, orders: [] };
                  agg[it.product_id].qty += it.qty;
                  agg[it.product_id].orders.push(it.order_id);
                });
                const rows = Object.values(agg);
                return (
                  <div key={cat} className="border-2 border-[var(--ink)] p-3 break-inside-avoid" data-testid={`pl-cat-${cat}`}>
                    <h3 className="font-display text-lg mb-2 bg-[var(--ink)] text-[var(--paper)] inline-block px-2 py-1">{cat.toUpperCase()}</h3>
                    <table className="w-full font-mono text-sm">
                      <thead className="border-b border-[var(--ink-2)]">
                        <tr className="text-left text-xs uppercase">
                          <th className="py-1 w-12">Qty</th>
                          <th className="py-1">Item</th>
                          <th className="py-1">For Orders</th>
                          <th className="py-1 w-16 text-right">Pick ☐</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(r => (
                          <tr key={r.product_id} className="border-b border-[var(--muted)]">
                            <td className="py-1.5 font-bold">×{r.qty}</td>
                            <td className="py-1.5">{r.name}</td>
                            <td className="py-1.5 text-xs">{[...new Set(r.orders)].join(", ")}</td>
                            <td className="py-1.5 text-right"><span className="inline-block w-5 h-5 border-2 border-[var(--ink)]"></span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          </section>

          <div className="mt-10 pt-4 border-t-2 border-[var(--ink)] font-mono text-xs flex justify-between">
            <div>Picker signature: ________________________</div>
            <div>Checked by: ________________________</div>
          </div>
        </>
      )}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .grain::before { display: none !important; }
          header { display: none !important; }
          .page-break { page-break-before: always; }
          .break-inside-avoid { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}
